import { music } from './MusicSystem';

export interface LocalMusicSnapshot {
  tracks: string[];
  index: number;
  playing: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  shuffle: boolean;
}

interface PersistedTrack {
  id: number;
  name: string;
  blob: Blob;
}

/**
 * A browser-local playlist for commander-provided music. Files are retained in
 * browser IndexedDB (never uploaded) so a commander does not need to select
 * the same tracks again after moving between missions or refreshing the game.
 */
class LocalMusicPlayer {
  private static readonly DB_NAME = 'apogee_hold_local_music';
  private static readonly STORE = 'tracks';
  private static readonly INDEX_KEY = 'apogee_hold_local_music_index';
  private audio: HTMLAudioElement | null = null;
  private tracks: { name: string; url: string; blob: Blob }[] = [];
  private index = 0;
  private volume = 1;
  private listeners = new Set<(state: LocalMusicSnapshot) => void>();
  private externalMusicActive = false;
  private playlistGeneration = 0;
  private shuffle = true;

  constructor() {
    if (typeof Audio === 'undefined') return;
    this.audio = new Audio();
    this.audio.preload = 'metadata';
    this.audio.volume = this.volume;
    this.audio.addEventListener('timeupdate', () => this.notify());
    this.audio.addEventListener('loadedmetadata', () => this.notify());
    this.audio.addEventListener('play', () => this.notify());
    this.audio.addEventListener('pause', () => this.notify());
    this.audio.addEventListener('ended', () => {
      if (this.tracks.length > 1 && this.shuffle) {
        let next = Math.floor(Math.random() * this.tracks.length);
        if (next === this.index) next = (next + 1) % this.tracks.length;
        this.select(next, true);
      } else if (this.index + 1 < this.tracks.length) this.select(this.index + 1, true);
      else this.stop();
    });
    void this.restore();
  }

  onChange(listener: (state: LocalMusicSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  load(files: FileList | File[]): void {
    const incoming = Array.from(files).filter((file) => {
      const audioType = file.type.startsWith('audio/');
      const audioName = /\.(aac|flac|m4a|mp3|ogg|wav|webm)$/i.test(file.name);
      return audioType || audioName;
    });
    if (!incoming.length) return;
    const existing = this.tracks.map((track) => ({ name: track.name, blob: track.blob }));
    const seen = new Set(existing.map((track) => track.name));
    const additions = incoming.filter((file) => !seen.has(file.name)).map((file) => ({ name: file.name, blob: file }));
    if (!additions.length) return;
    // Mark the playlist as user-owned only once there is a real change. This
    // keeps an in-flight restore from being cancelled by a duplicate selection.
    this.playlistGeneration++;
    const current = this.index;
    this.setTracks([...existing, ...additions], current);
    void this.persist([...existing, ...additions]);
  }

  toggleShuffle(): void {
    this.shuffle = !this.shuffle;
    this.notify();
  }

  get isShuffle(): boolean { return this.shuffle; }

  /** Keep commander-provided tracks on the same master music-volume setting. */
  setVolume(value: number): void {
    this.volume = Math.max(0, Math.min(1, value));
    if (this.audio) this.audio.volume = this.volume;
    this.notify();
  }

  toggle(): void {
    if (!this.audio || !this.tracks.length) return;
    if (this.audio.paused) {
      this.startPlayback();
    } else {
      this.audio.pause();
    }
    this.notify();
  }

  stop(): void {
    if (!this.audio) return;
    this.audio.pause();
    this.audio.currentTime = 0;
    if (this.externalMusicActive) {
      this.externalMusicActive = false;
      music.setExternalPlaybackActive(false);
    }
    this.notify();
  }

  previous(): void {
    if (this.index > 0) this.select(this.index - 1, !this.audio?.paused);
  }

  next(): void {
    if (this.tracks.length < 2) return;
    // Treat the local playlist as a queue: skipping at the end wraps to the
    // first track instead of silently doing nothing on the menu card.
    this.select((this.index + 1) % this.tracks.length, !this.audio?.paused);
  }

  selectTrack(index: number): void {
    if (index < 0 || index >= this.tracks.length) return;
    this.select(index, !this.audio?.paused);
  }

  remove(index: number): void {
    if (index < 0 || index >= this.tracks.length) return;
    const remaining = this.tracks
      .filter((_track, trackIndex) => trackIndex !== index)
      .map((track) => ({ name: track.name, blob: track.blob }));
    if (!remaining.length) {
      this.playlistGeneration++;
      this.setTracks([], 0);
      void this.persist([]);
      return;
    }
    const nextIndex = this.index > index ? this.index - 1 : Math.min(this.index, remaining.length - 1);
    this.playlistGeneration++;
    this.setTracks(remaining, nextIndex);
    void this.persist(remaining);
  }

  move(from: number, to: number): void {
    if (from < 0 || to < 0 || from >= this.tracks.length || to >= this.tracks.length || from === to) return;
    const next = this.tracks.map((track) => ({ name: track.name, blob: track.blob }));
    const [track] = next.splice(from, 1);
    next.splice(to, 0, track);
    const currentName = this.tracks[this.index]?.name;
    const nextIndex = Math.max(0, next.findIndex((candidate) => candidate.name === currentName));
    this.playlistGeneration++;
    this.setTracks(next, nextIndex);
    void this.persist(next);
  }

  seek(fraction: number): void {
    if (!this.audio || !Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;
    this.audio.currentTime = Math.max(0, Math.min(1, fraction)) * this.audio.duration;
    this.notify();
  }

  snapshot(): LocalMusicSnapshot {
    return {
      tracks: this.tracks.map((track) => track.name),
      index: this.index,
      playing: !!this.audio && !this.audio.paused,
      currentTime: this.audio?.currentTime ?? 0,
      duration: Number.isFinite(this.audio?.duration) ? this.audio?.duration ?? 0 : 0,
      volume: this.volume,
      shuffle: this.shuffle,
    };
  }

  private select(index: number, autoplay: boolean): void {
    if (!this.audio || !this.tracks[index]) return;
    this.index = index;
    try { localStorage.setItem(LocalMusicPlayer.INDEX_KEY, String(index)); } catch { /* storage unavailable */ }
    this.audio.src = this.tracks[index].url;
    this.audio.load();
    if (autoplay) this.startPlayback();
    this.notify();
  }

  /** Start a local track and always restore the procedural score if playback
   * is rejected (unsupported codec, revoked blob URL, or browser policy). */
  private startPlayback(): void {
    if (!this.audio) return;
    // Master music toggle is off — never resume commander tracks on load/toggle.
    if (!music.enabled || this.volume <= 0) {
      this.audio.pause();
      return;
    }
    this.externalMusicActive = true;
    music.setExternalPlaybackActive(true);
    void this.audio.play().catch(() => {
      this.externalMusicActive = false;
      music.setExternalPlaybackActive(false);
      this.notify();
    });
  }

  private notify(): void {
    const state = this.snapshot();
    this.listeners.forEach((listener) => listener(state));
  }

  private setTracks(tracks: { name: string; blob: Blob }[], index: number): void {
    this.stop();
    this.tracks.forEach((track) => URL.revokeObjectURL(track.url));
    this.tracks = tracks.map((track) => ({ name: track.name, blob: track.blob, url: URL.createObjectURL(track.blob) }));
    this.index = Math.max(0, Math.min(index, Math.max(0, this.tracks.length - 1)));
    try { localStorage.setItem(LocalMusicPlayer.INDEX_KEY, String(this.index)); } catch { /* storage unavailable */ }
    if (this.audio && this.tracks[this.index]) {
      this.audio.src = this.tracks[this.index].url;
      this.audio.load();
    }
    this.notify();
  }

  private async database(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return null;
    return new Promise((resolve) => {
      const request = indexedDB.open(LocalMusicPlayer.DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(LocalMusicPlayer.STORE)) {
          request.result.createObjectStore(LocalMusicPlayer.STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    });
  }

  private async persist(files: { name: string; blob: Blob }[]): Promise<void> {
    const db = await this.database();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const transaction = db.transaction(LocalMusicPlayer.STORE, 'readwrite');
      const store = transaction.objectStore(LocalMusicPlayer.STORE);
      store.clear();
      files.forEach((file, id) => store.put({ id, name: file.name, blob: file.blob } satisfies PersistedTrack));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
    db.close();
  }

  private async restore(): Promise<void> {
    const db = await this.database();
    if (!db) return;
    const tracks = await new Promise<PersistedTrack[]>((resolve) => {
      const transaction = db.transaction(LocalMusicPlayer.STORE, 'readonly');
      const request = transaction.objectStore(LocalMusicPlayer.STORE).getAll();
      request.onsuccess = () => resolve((request.result as PersistedTrack[]).sort((a, b) => a.id - b.id));
      request.onerror = () => resolve([]);
    });
    db.close();
    // A manual selection always wins over an asynchronous restoration.
    if (this.playlistGeneration === 0 && tracks.length) {
      let index = 0;
      try { index = Number(localStorage.getItem(LocalMusicPlayer.INDEX_KEY)) || 0; } catch { /* storage unavailable */ }
      this.setTracks(tracks, index);
    }
  }
}

export const localMusic = new LocalMusicPlayer();
